import { useTranslation } from 'react-i18next'
import { useAuth } from '../../../context/auth-context'
import { useCurrency } from '../../../context/currency-context'
import { useAmountAndCurrency } from '../../../shared/useAmountAndCurrency'
import { ObjectivePayment, ObjectiveDto, OneOffPayment, TargetCorrectionPaymentDto, Currency } from '../../../types'

import BackgroundBox from '../BackgroundBox'
import PerFlexRow from '../FlexRow'
import PerText from '../Text'
import { retrieveCurrencyConversionRate } from '../../../shared/currencyConversionUtil'
import { Tooltip } from 'antd'

interface MeetingStatusModalPaymentFooter {
  objectives?: ObjectiveDto[]
  objectivePayment?: ObjectivePayment
  oneOffPayment?: OneOffPayment
  targetCorrectionPayment?: TargetCorrectionPaymentDto
}

export const MeetingStatusModalPaymentFooter = ({
  objectives,
  objectivePayment,
  oneOffPayment,
  targetCorrectionPayment,
}: MeetingStatusModalPaymentFooter): JSX.Element => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { currency, currencyConversionData } = useCurrency()
  const { formatCurrency, calculateAndFormatNewTargetAmount } = useAmountAndCurrency(user.locale)

  const unknownCurrenciesForConversion: Currency[] = !!objectives
    ? [
        ...new Set(
          objectives
            .filter((obj): boolean => {
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              if (obj.currency! !== currency) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const conversionRate: number | undefined = retrieveCurrencyConversionRate(obj.currency!, currency, currencyConversionData)
                if (!conversionRate) {
                  return true
                }
              }
              return false
            })
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            .map((obj) => obj.currency!),
        ),
      ]
    : []

  const achievedTargets =
    !!objectives && objectives.length
      ? objectives.reduce((acc: number, current: ObjectiveDto) => {
          if (current.currency !== currency) {
            if (!current.achievedAmount) {
              return acc
            }
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            if (unknownCurrenciesForConversion.includes(current.currency!)) {
              return (
                acc +
                Number(
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  (current.achievedAmount * retrieveCurrencyConversionRate(current.currency!, currency, currencyConversionData)!).toFixed(
                    2,
                  ),
                )
              )
            }
            return acc
          }
          return acc + (current.achievedAmount || 0)
        }, 0)
      : 0

  const deduction = !!objectivePayment && objectivePayment.deduction ? objectivePayment.deduction : 0

  const oneOff = !!oneOffPayment && oneOffPayment.resultAmount ? oneOffPayment.resultAmount : 0

  const targetCorrection = !!targetCorrectionPayment && !!targetCorrectionPayment.resultAmount ? targetCorrectionPayment.resultAmount : 0

  const toBePaid = achievedTargets + oneOff + targetCorrection - deduction

  return (
    <BackgroundBox r noShadow color="gray-2" className="appraisal-footer fullWidth">
      <PerFlexRow align="middle" justify="space-between" className="fullWidth">
        <PerFlexRow align="middle">
          {(!!achievedTargets || !!unknownCurrenciesForConversion.length) && (
            <PerFlexRow>
              <div>
                <PerText appearance="footer-label" testId="modal-payment-footer-achieved-label">
                  {t('appraisalStatusModal.achievedTargets')}
                </PerText>
                {!!deduction && (
                  <PerText appearance="footer-label" testId="modal-payment-footer-deduction-label">
                    {t('appraisalStatusModal.manualDeduction')}
                  </PerText>
                )}
              </div>
              <div>
                <PerText
                  appearance="footer-input"
                  testId="modal-payment-footer-achieved-value"
                  color={!!unknownCurrenciesForConversion.length ? 'error' : undefined}
                  fontWeight={unknownCurrenciesForConversion.length ? 'bold' : undefined}
                >
                  <Tooltip
                    title={
                      !!unknownCurrenciesForConversion.length
                        ? t('appraisalStatusModal.unknownConversionRate', {
                            currentContractCurrency: currency,
                            unknownCurrencies: unknownCurrenciesForConversion.join(', '),
                          })
                        : undefined
                    }
                  >
                    {!!unknownCurrenciesForConversion.length
                      ? calculateAndFormatNewTargetAmount(achievedTargets, currency)
                      : formatCurrency(achievedTargets, currency, true)}
                  </Tooltip>
                </PerText>
                {!!deduction && (
                  <PerText appearance="footer-input" testId="modal-payment-footer-deduction-value">
                    {formatCurrency(deduction, currency, true)}
                  </PerText>
                )}
              </div>
            </PerFlexRow>
          )}
          {(!!oneOffPayment || !!targetCorrection) && (
            <PerFlexRow ml={!!achievedTargets || !!unknownCurrenciesForConversion.length ? 'xxxl' : 'none'}>
              <div>
                {!!oneOffPayment && (
                  <PerText appearance="footer-label" testId="modal-payment-footer-oneoffpayment-label">
                    {t('appraisalStatusModal.oneOffPayment')}
                  </PerText>
                )}
                {!!targetCorrection && (
                  <PerText appearance="footer-label" testId="modal-payment-footer-targetcorrection-label">
                    {t('appraisalStatusModal.targetCorrectionPayment')}
                  </PerText>
                )}
              </div>
              <div>
                {!!oneOffPayment && (
                  <PerText appearance="footer-input" testId="modal-payment-footer-oneoffpayment-value">
                    {formatCurrency(oneOff, currency, true)}
                  </PerText>
                )}
                {!!targetCorrection && (
                  <PerText appearance="footer-input" testId="modal-payment-footer-targetcorrection-value">
                    {formatCurrency(targetCorrection, currency, true)}
                  </PerText>
                )}
              </div>
            </PerFlexRow>
          )}
        </PerFlexRow>
        <PerFlexRow align="middle">
          <div>
            <PerText appearance="footer-label">{t('appraisalStatusModal.toBePaid')}</PerText>
          </div>
          <div>
            <PerText
              appearance="footer-input"
              color={!!unknownCurrenciesForConversion.length ? 'error' : undefined}
              fontWeight={unknownCurrenciesForConversion.length ? 'bold' : undefined}
            >
              <Tooltip
                title={
                  !!unknownCurrenciesForConversion.length
                    ? t('appraisalStatusModal.unknownConversionRate', {
                        currentContractCurrency: currency,
                        unknownCurrencies: unknownCurrenciesForConversion.join(', '),
                      })
                    : undefined
                }
              >
                {!!unknownCurrenciesForConversion.length
                  ? calculateAndFormatNewTargetAmount(toBePaid, currency)
                  : formatCurrency(toBePaid, currency, true)}
              </Tooltip>
            </PerText>
          </div>
        </PerFlexRow>
      </PerFlexRow>
    </BackgroundBox>
  )
}
